/** Part kinds for home scene authoring — pick kind, system fills rules. */

export const HOME_PART_KINDS = [
  "backdrop",
  "shell",
  "floor",
  "solid",
  "prop",
  "platform",
  "attack_dummy",
  "monster",
] as const;

export type HomePartKind = (typeof HOME_PART_KINDS)[number];

export type HomeShopTab = "all" | HomePartKind;

/** Default max placements per kind in one room (backdrop, shell, solid stay 1). */
export const HOME_KIND_QUOTA_DEFAULT = 240;

/** Order in category pickers (user-facing). */
export const KIND_PICKER_KINDS: HomePartKind[] = [
  "solid",
  "prop",
  "attack_dummy",
  "monster",
  "floor",
  "shell",
  "platform",
  "backdrop",
];

export type KindPreset = {
  walkable: boolean;
  stackable: boolean;
  occupies: boolean;
  depthBase: number;
  labelTh: string;
  hintTh: string;
  tabLabelTh: string;
  /** Soft quota per room. */
  quota: number;
};

export const KIND_PRESETS: Record<HomePartKind, KindPreset> = {
  backdrop: {
    walkable: false,
    stackable: true,
    occupies: false,
    depthBase: -10,
    labelTh: "บรรยากาศในห้อง",
    hintTh: "ภาพหลังในกรอบเล่น · ไม่กินกริด",
    tabLabelTh: "บรรยากาศ",
    quota: 1,
  },
  shell: {
    walkable: false,
    stackable: true,
    occupies: false,
    depthBase: -100,
    labelTh: "ฉาก",
    hintTh: "พื้นหลังนอกกรอบกริด (รอบ canvas) · ไม่วางในฉาก",
    tabLabelTh: "ฉาก",
    quota: 1,
  },
  floor: {
    walkable: true,
    stackable: true,
    occupies: false,
    depthBase: 0,
    labelTh: "พื้น / ทางเดิน",
    hintTh: "กระเบื้องสี่เหลี่ยมจัตุรัส · วางแล้วอยู่ชั้นหลัง · เดินได้",
    tabLabelTh: "พื้น",
    quota: HOME_KIND_QUOTA_DEFAULT,
  },
  solid: {
    walkable: false,
    stackable: false,
    occupies: true,
    depthBase: 10,
    labelTh: "บ้าน / โครงสร้าง",
    hintTh: "ตึก กำแพง · เดินทะลุไม่ได้ · วางในฉากได้ชิ้นเดียว",
    tabLabelTh: "บ้าน",
    quota: 1,
  },
  prop: {
    walkable: false,
    stackable: true,
    occupies: false,
    depthBase: 20,
    labelTh: "ของตกแต่ง",
    hintTh: "ต้นไม้ ของเล็ก · วางทับบ้านได้",
    tabLabelTh: "ตกแต่ง",
    quota: HOME_KIND_QUOTA_DEFAULT,
  },
  platform: {
    walkable: true,
    stackable: false,
    occupies: true,
    depthBase: 12,
    labelTh: "แท่น / ที่สูง",
    hintTh: "ยืนหรือกระโดดขึ้นได้",
    tabLabelTh: "แท่น",
    quota: HOME_KIND_QUOTA_DEFAULT,
  },
  attack_dummy: {
    walkable: false,
    stackable: true,
    occupies: false,
    depthBase: 22,
    labelTh: "หุ่นทดสอบการโจมตี",
    hintTh: "เป้าฝึกโจมตี · วางบนพื้นหรือทับบ้านได้",
    tabLabelTh: "หุ่นฝึก",
    quota: HOME_KIND_QUOTA_DEFAULT,
  },
  monster: {
    walkable: false,
    stackable: true,
    occupies: true,
    depthBase: 25,
    labelTh: "มอนสเตอร์",
    hintTh: "ศัตรูในห้อง · จองช่องพื้น",
    tabLabelTh: "มอนส์",
    quota: HOME_KIND_QUOTA_DEFAULT,
  },
};

export const HOME_SHOP_TABS: { id: HomeShopTab; labelTh: string }[] = [
  { id: "all", labelTh: "ทั้งหมด" },
  { id: "shell", labelTh: KIND_PRESETS.shell.tabLabelTh },
  { id: "backdrop", labelTh: KIND_PRESETS.backdrop.tabLabelTh },
  { id: "floor", labelTh: KIND_PRESETS.floor.tabLabelTh },
  { id: "solid", labelTh: KIND_PRESETS.solid.tabLabelTh },
  { id: "prop", labelTh: KIND_PRESETS.prop.tabLabelTh },
  { id: "platform", labelTh: KIND_PRESETS.platform.tabLabelTh },
  { id: "attack_dummy", labelTh: KIND_PRESETS.attack_dummy.tabLabelTh },
  { id: "monster", labelTh: KIND_PRESETS.monster.tabLabelTh },
];

const DECOR_KINDS = new Set<HomePartKind>(["prop", "attack_dummy"]);
const STRUCTURE_KINDS = new Set<HomePartKind>(["solid", "platform", "monster"]);

export function isHomePartKind(value: unknown): value is HomePartKind {
  return typeof value === "string" && (HOME_PART_KINDS as readonly string[]).includes(value);
}

export function normalizePartKind(value: unknown): HomePartKind {
  return isHomePartKind(value) ? value : "solid";
}

export function kindPreset(kind: HomePartKind): KindPreset {
  return KIND_PRESETS[kind];
}

export function resolvePartFlags(kind: HomePartKind): {
  walkable: boolean;
  stackable: boolean;
  occupies: boolean;
} {
  const preset = kindPreset(kind);
  return {
    walkable: preset.walkable,
    stackable: preset.stackable,
    occupies: preset.occupies,
  };
}

export function kindBadgeLabels(kind: HomePartKind): string[] {
  const p = kindPreset(kind);
  const badges: string[] = [p.tabLabelTh];
  if (kind === "attack_dummy") badges.push("เป้าโจมตี");
  if (kind === "monster") badges.push("ศัตรู");
  if (p.walkable) badges.push("เดินได้");
  if (p.stackable && kind !== "backdrop" && kind !== "shell" && kind !== "floor") {
    badges.push("ซ้อนได้");
  }
  return badges;
}

/** Whether two overlapping parts may share the same cells. */
export function canStackKinds(incoming: HomePartKind, existing: HomePartKind): boolean {
  if (incoming === "backdrop" || existing === "backdrop") return true;
  if (incoming === "shell" || existing === "shell") return true;
  if (incoming === "floor" || existing === "floor") return true;
  if (kindPreset(existing).walkable) return true;
  if (DECOR_KINDS.has(incoming) && (STRUCTURE_KINDS.has(existing) || DECOR_KINDS.has(existing))) {
    return true;
  }
  if (DECOR_KINDS.has(existing) && STRUCTURE_KINDS.has(incoming)) {
    return true;
  }
  if (!kindPreset(incoming).occupies || !kindPreset(existing).occupies) {
    return true;
  }
  return false;
}

/** Edit grid draws at depth 1 — grid-placed sprites must not sort below it. */
const MIN_GRID_PLACEMENT_DEPTH = 1.02;

export function minPlacementDepthBias(
  kind: HomePartKind,
  gridY: number,
  cellsH: number,
  globalMin = -48,
): number {
  if (kind === "backdrop" || kind === "shell") return globalMin;
  const base = kindPreset(kind).depthBase + gridY * 10 + cellsH;
  return Math.max(globalMin, Math.ceil(MIN_GRID_PLACEMENT_DEPTH - base));
}

export function clampPlacementDepthBiasForGrid(
  kind: HomePartKind,
  gridY: number,
  cellsH: number,
  bias: number,
  globalMin = -48,
  globalMax = 48,
): number {
  const v = Math.round(bias);
  const clamped = Math.max(globalMin, Math.min(globalMax, v));
  return Math.max(minPlacementDepthBias(kind, gridY, cellsH, globalMin), clamped);
}

export function placementDepth(
  kind: HomePartKind,
  gridY: number,
  cellsH: number,
  extraBias = 0,
): number {
  const raw = kindPreset(kind).depthBase + gridY * 10 + cellsH + extraBias;
  if (kind === "backdrop" || kind === "shell") return raw;
  return Math.max(MIN_GRID_PLACEMENT_DEPTH, raw);
}

/** Play-mode player — slightly above furniture at the same feet row. */
export function playerSortDepth(feetGridRow: number): number {
  return placementDepth("solid", feetGridRow, 1, 8);
}

export function kindSelectOptionsHtml(selected: HomePartKind = "solid"): string {
  return KIND_PICKER_KINDS.map((kind) => {
    const p = kindPreset(kind);
    const sel = kind === selected ? " selected" : "";
    return `<option value="${kind}"${sel}>${p.labelTh}</option>`;
  }).join("");
}
